// Hideo - script comments: the column (src/components/editor/CommentsPanel.vue), its buttons and keys, and starting a
// comment from the selection. Ported from the legacy comments.js with the same moments: the column is drawn when
// render() is called (opening, tab, every action), from a copy of the comments taken then, and drawing first removes
// the highlights left without a comment (the highlights are spans in the page). The model and the highlights are
// src/features/editor/comments.ts. The writing surface (src/features/editor/surface/) calls start, cancel, focus, toggle.
// Select some text and press "Comment" (toolbar, selection menu or Ctrl+Alt+M). The commented text is wrapped in
// <span class="cmt" data-cmt="<id>"> inside the tab's HTML; the comment lives in script.comments. Resolving or
// deleting a comment removes the highlight from the text (the text stays). If the commented text is deleted, the
// comment stays with a "text deleted" note. The active comment is highlighted with a <style> (#cmtHl), not a class, so
// nothing temporary ends up in the saved HTML.
import { defineStore } from 'pinia'
import { nextTick, ref, shallowRef } from 'vue'
import { t } from '../i18n'
import { uiAlert, uiConfirm } from './notices'
import { useScriptsStore } from './scripts'
import { useEditorStore } from './editor'
import { useDialogsStore } from './dialogs'
import { edBodyEl, noZw, setEdTop } from '../features/editor/html'
import { savedRange } from '../features/editor/surface/core'
import {
  cmtAdd, cmtAuthor, cmtCleanupIn, cmtEdited, cmtNewId, cmtOrder, cmtQuote, cmtRemove, cmtReplied, cmtResolved, cmtSort,
  cmtSpans, cmtUnwrap, cmtWrap, tabComments
} from '../features/editor/comments'
import type { Script, ScriptComment } from '../data/types'
import { saveAppData } from '../features/storage/save'

/** The column as drawn: the open comments in the order of the text, the resolved ones, the order of the highlights */
interface CommentsView { open: ScriptComment[]; done: ScriptComment[]; order: string[] }

const field = (id: string) => document.getElementById(id) as HTMLTextAreaElement | null

export const useCommentsStore = defineStore('comments', () => {
  const scripts = useScriptsStore()
  /** The comment being written (its highlight is already in the page) */
  const draft = ref<{ id: string; quote: string } | null>(null)
  const active = ref('')
  const showResolved = ref(false)
  const editing = ref('')
  const replying = ref('')
  /** Open over the page (below 1280 px: .cmt-open) */
  const open = ref(false)
  /** The column as last drawn (null = never drawn); seq grows at every drawing (the column is drawn again) */
  const view = shallowRef<CommentsView | null>(null)
  const seq = ref(0)

  const list = (): ScriptComment[] => tabComments(scripts.current()[1])
  const mode = () => useEditorStore().mode

  /** New comment: highlights the selected text at once and opens the box to write it */
  function start(): unknown {
    if (!scripts.editingId) return
    if (mode() !== 'visual') return uiAlert(t('Comments are added in the Visual view.'))
    const s = getSelection() as Selection, inBody = s.rangeCount && !s.isCollapsed && edBodyEl().contains(s.getRangeAt(0).commonAncestorContainer)
    const saved = savedRange, r = inBody ? s.getRangeAt(0) : (saved && !saved.collapsed ? saved : null)
    if (!r || !noZw(r.toString()).trim()) return uiAlert(t('First select the piece of text to comment on.'))
    cancel()
    const id = cmtNewId(), texts = cmtWrap(r.cloneRange(), id)
    if (!texts.length) return uiAlert(t("This part can't be commented on: try selecting normal text."))
    const quote = cmtQuote(texts)
    s.removeAllRanges(); draft.value = { id, quote }; active.value = id; editing.value = replying.value = ''
    toggle(true); render()
    setTimeout(() => { const ta = field('cmtNewText'); if (ta) ta.focus() }, 60)
  }
  /** The comment being written is dropped (with its highlight) */
  function cancel(): void {
    if (!draft.value) return
    cmtUnwrap(draft.value.id); draft.value = null; useEditorStore().queueSave(); render()
  }
  function saveNew(): void {
    const ta = field('cmtNewText'), txt = ta ? ta.value.trim() : ''
    if (!draft.value || !txt) { if (ta) ta.focus(); return }
    const { id, quote } = draft.value
    draft.value = null
    useEditorStore().save()   // the HTML with the highlight is saved before the comment
    const [d, sc] = scripts.current()
    if (!sc) return
    cmtAdd(sc, { id, quote }, txt, cmtAuthor(), Date.now())
    saveAppData(d, true); active.value = id; render()
  }
  /** Changes a comment's data: the script is saved first (highlights), then the comment */
  function update(id: string, fn: (c: ScriptComment, sc: Script) => unknown): void {
    useEditorStore().save()
    const [d, sc] = scripts.current()
    if (!sc) return
    const c = (sc.comments || []).find(x => x.id === id)
    if (!c) return
    if (fn(c, sc) !== false) saveAppData(d, true)
    render()
  }
  /** Resolved: archived, its highlight removed */
  function resolve(id: string): void {
    cmtUnwrap(id); if (active.value === id) active.value = ''
    update(id, c => cmtResolved(c, Date.now()))
  }
  async function remove(id: string): Promise<void> {
    if (!(await uiConfirm(t('Delete this comment and its replies?'), { ok: t('Delete'), danger: true, icon: 'fa-comment-slash' }))) return
    cmtUnwrap(id); if (active.value === id) active.value = ''
    update(id, (_c, sc) => cmtRemove(sc, id))
  }
  function editStart(id: string): void {
    editing.value = id; replying.value = ''; active.value = id; render()
    setTimeout(() => { const ta = field('cmtEditText'); if (ta) { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length) } }, 30)
  }
  function editSave(id: string): void {
    const txt = (field('cmtEditText') || { value: '' }).value
    editing.value = ''
    if (!txt || !txt.trim()) return render()
    update(id, c => cmtEdited(c, txt, Date.now()))
  }
  function replyStart(id: string): void {
    replying.value = id; editing.value = ''; active.value = id; render()
    setTimeout(() => { const ta = field('cmtReplyText'); if (ta) ta.focus() }, 30)
  }
  function replySave(id: string): void {
    const txt = (field('cmtReplyText') || { value: '' }).value
    replying.value = ''
    if (!txt || !txt.trim()) return render()
    update(id, c => cmtReplied(c, txt, cmtAuthor(), Date.now()))
  }
  /** Edit or reply cancelled */
  function closeBox(): void { editing.value = replying.value = ''; render() }
  /** Keys in the comment fields: Ctrl+Enter = save, Esc = cancel (without closing the editor) */
  function key(e: KeyboardEvent, kind: 'new' | 'edit' | 'reply', id: string): void {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); if (kind === 'new') cancel(); else closeBox(); return }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); if (kind === 'new') saveNew(); else if (kind === 'edit') editSave(id); else replySave(id) }
  }

  /** The active comment highlighted in the text (#cmtHl) */
  function highlight(): void {
    let st = document.getElementById('cmtHl')
    if (!st) { st = document.createElement('style'); st.id = 'cmtHl'; document.head.appendChild(st) }
    st.textContent = active.value ? `.script-body .cmt[data-cmt="${CSS.escape(active.value)}"]{background:rgb(var(--brand) / .34);border-bottom-color:rgb(var(--brand))}` : ''
  }
  /** Click on commented text: its comment, in the column */
  function focus(id: string): void {
    active.value = id; toggle(true); render()
    nextTick(() => {
      const card = document.querySelector(`#scriptCmts [data-card="${CSS.escape(id)}"]`)
      if (card) card.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
  }
  /** Click on a comment: its text, in the page */
  function go(id: string): void {
    active.value = id; render()
    const sp = cmtSpans(id)[0]
    if (sp) sp.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }
  /** Removes the highlights without a comment (e.g. a comment started and never saved before closing) */
  function cleanup(): void {
    const sc = scripts.current()[1]
    if (!sc) return
    const n = cmtCleanupIn(edBodyEl(), sc.comments, draft.value && draft.value.id)
    if (draft.value && !cmtSpans(draft.value.id).length) draft.value = null
    if (n) useEditorStore().queueSave()
  }
  /** Opens or closes the column over the page (on = toggles); opening draws it again */
  function toggle(on?: boolean): void {
    on = on ?? !open.value
    open.value = on
    if (on) { setEdTop(); render() }
  }
  /** Draws the column (only with a script open) */
  function render(): void {
    if (!scripts.editingId) return
    if (mode() === 'visual') cleanup()
    const order = cmtOrder(edBodyEl()), { open: o, done } = cmtSort(list(), order)
    view.value = JSON.parse(JSON.stringify({ open: o, done, order })) as CommentsView
    seq.value++
    highlight()
  }
  function toggleResolved(): void { showResolved.value = !showResolved.value; render() }

  /** Esc closes the column open over the page (narrow screens) before closing the editor; not while the teleprompter
   *  is on top. Window, capture phase: added by ScriptEditor.vue once mounted, after the writing surface's (main.ts) */
  function escListener(e: KeyboardEvent): void {
    if (e.key !== 'Escape' || !useEditorStore().shown || useDialogsStore().isOpen('teleprompter') || /^(TEXTAREA|INPUT)$/.test((e.target as HTMLElement).tagName)) return
    if (open.value && innerWidth < 1280) { e.stopPropagation(); e.preventDefault(); toggle(false) }
  }

  return {
    draft, active, showResolved, editing, replying, open, view, seq,
    list, start, cancel, saveNew, update, resolve, remove, editStart, editSave, replyStart, replySave, closeBox, key,
    highlight, focus, go, cleanup, toggle, render, toggleResolved, escListener
  }
})
